<template>
    <NuxtLink :to="mentorLink" class="mentor-card shrink-0 w-[300px] block cursor-pointer">
        <!-- Image -->
        <div class="relative h-[220px] rounded-t-3xl overflow-hidden bg-gray-200 dark:bg-surface-subtle">
            <CThumbnailImage
                v-if="mentor.imageUrl"
                :src="mentor.imageUrl"
                :alt="mentor.name"
                type="profile"
                class="size-full !aspect-auto !rounded-none"
            />
            <div v-else class="size-full flex items-center justify-center bg-gradient-to-br from-teal-100 to-teal-200 dark:from-teal-900/30 dark:to-teal-900/30">
                <span class="text-4xl">👤</span>
            </div>
            <!-- Match Score Badge -->
            <div
                v-if="mentor.matchScore"
                class="absolute top-3 right-3 bg-white/90 dark:bg-surface-card/90 backdrop-blur-sm rounded-full px-2.5 py-1 shadow-sm"
            >
                <span class="text-sm font-semibold text-purple-600 dark:text-purple-400">{{ mentor.matchScore }}%</span>
            </div>
        </div>

        <!-- Content -->
        <div class="bg-white dark:bg-surface-card p-4 rounded-b-3xl flex flex-col gap-6">
            <div class="flex flex-col gap-3">
                <!-- Name, role, and bio -->
                <div class="flex flex-col gap-1">
                    <h3 class="font-semibold text-lg text-[#1d191f] dark:text-white tracking-tight leading-normal">
                        {{ mentor.name }}
                    </h3>
                    <p
                        v-if="mentor.title"
                        class="text-sm text-[#6c5d73] dark:text-txt-body leading-normal flex items-start gap-1.5"
                    >
                        <CIcon name="jobs" class="size-4 text-gray-400 shrink-0 mt-0.5" />
                        <span>{{ mentor.title }}</span>
                    </p>
                    <p v-if="description" class="text-sm text-[#6c5d73] dark:text-txt-body leading-snug line-clamp-2">
                        {{ description }}
                    </p>
                </div>

                <!-- Tags -->
                <div class="tags-container line-clamp-2">
                    <span
                        v-for="tag in mentor.tags"
                        :key="tag"
                        class="font-semibold text-sm text-[#1d191f] dark:text-white leading-5 inline"
                    >
                        <span class="text-[#047274] dark:text-primary-400">#</span>{{ tag }}&nbsp;&nbsp;
                    </span>
                </div>
            </div>
        </div>
    </NuxtLink>
</template>

<script setup lang="ts">
import CIcon from '@/components/ui/CIcon.vue'
import CThumbnailImage from '@/components/ui/CThumbnailImage.vue'

interface Mentor {
    name: string
    title?: string
    description?: string
    imageUrl?: string
    tags: string[]
    slug?: string
    id?: string
    matchScore?: number
}

const props = defineProps<{
    mentor: Mentor
}>()

function plainText(value?: string): string {
    return (value || '')
        .replace(/<script\b[^<]*(?:(?!<\/script>)<[^<]*)*<\/script>/gi, ' ')
        .replace(/<style\b[^<]*(?:(?!<\/style>)<[^<]*)*<\/style>/gi, ' ')
        .replace(/<[^>]*>/g, ' ')
        .replace(/&nbsp;/g, ' ')
        .replace(/&amp;/g, '&')
        .replace(/&quot;/g, '"')
        .replace(/&#39;/g, "'")
        .replace(/\s+/g, ' ')
        .trim()
}

const description = computed(() => {
    const sourceDescription = plainText(props.mentor.description)
    if (sourceDescription) return sourceDescription

    if (props.mentor.tags.length) {
        return `Mentoring v oblastiach ${props.mentor.tags.slice(0, 3).join(', ')}.`
    }

    return ''
})

const mentorLink = computed(() => {
    const slugOrId = props.mentor.slug || props.mentor.id
    const suffix = props.mentor.slug && props.mentor.id ? `?uid=${props.mentor.id}` : ''
    return `/mentor/${slugOrId}${suffix}`
})
</script>

<style scoped>
.mentor-card {
    transition: transform 0.2s ease, box-shadow 0.2s ease;
}

.mentor-card:hover {
    transform: translateY(-4px);
    box-shadow: 0 10px 25px -5px rgba(0, 0, 0, 0.1);
}

.mentor-card :deep(.CThumbnailImage) {
    aspect-ratio: auto;
    border-radius: 0;
}
</style>
